import { Button, Input, Label, Modal, Select } from '@mercai/clever'
import { useEffect, useRef, useState } from 'react'

import {
  SupplyOrderStatusCancelledReasonEnum,
  UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum,
} from '@/types'
import { useTranslation } from 'react-i18next'
import { ModalSupplyOrderCancellationReasonProps } from './modal-supply-order-cancellation-reason.type'

import { useValidationErrors } from '@/helpers'
import { schema } from './modal-supply-order-cancellation-reason.constants'
import styles from './modal-supply-order-cancellation-reason.module.css'

export const ModalSupplyOrderCancellationReason = ({
  onConfirm,
  isOpen,
  setIsOpen,
}: ModalSupplyOrderCancellationReasonProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const [action, setAction] =
    useState<
      keyof typeof UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum
    >()
  const [cancellationReason, setCancellationReason] =
    useState<keyof typeof SupplyOrderStatusCancelledReasonEnum>()
  const [cancellationDescription, setCancellationDescription] = useState<
    string | undefined
  >()

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schema(t),
    { cancellationReason, cancellationDescription, action },
  )

  const buttonRef = useRef<HTMLDivElement | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen?.(false)
    }
  }

  const onCancelCancellation = () => {
    setCancellationReason(undefined)
    setCancellationDescription(undefined)
    setIsOpen?.(false)
  }

  const onConfirmCancellation = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)

    onConfirm({
      action,
      cancellationReason,
      cancellationDescription,
    })

    setCancellationReason(undefined)
    setCancellationDescription(undefined)
    setAction(undefined)
    setIsOpen?.(false)
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Modal.Root
      isOpen={!!isOpen}
      onHandleOpen={onCancelCancellation}
      size="medium"
    >
      <Modal.Header
        title={t('componentModalSupplyOrderCancellationReasonTitle')}
      />
      <Modal.Body className={styles['modal-body']}>
        <div>
          <Label required>
            {t('componentModalSupplyOrderCancellationReasonActionLabel')}
          </Label>
          <Select
            options={Object.keys(
              UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum,
            ).map((key) => ({
              value: key,
              label: tEnums(
                `update_supply_order_on_sale_order_cancel_supply_orders_actions_${key}`,
              ),
            }))}
            value={action}
            onChange={(value) =>
              setAction(
                value as keyof typeof UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum,
              )
            }
            error={errors.action ? (errors.action as string) : ''}
            placeholder={t(
              'componentModalSupplyOrderCancellationReasonActionPlaceholder',
            )}
          />
        </div>

        <div>
          <Label required>
            {t('componentModalSupplyOrderCancellationReasonReasonLabel')}
          </Label>
          <Select
            options={Object.keys(SupplyOrderStatusCancelledReasonEnum).map(
              (key) => ({
                value: key,
                label: tEnums(`supply_order_status_cancelled_reason_${key}`),
              }),
            )}
            value={cancellationReason}
            onChange={(value) =>
              setCancellationReason(
                value as keyof typeof SupplyOrderStatusCancelledReasonEnum,
              )
            }
            error={
              errors.cancellationReason
                ? (errors.cancellationReason as string)
                : ''
            }
            placeholder={t(
              'componentModalSupplyOrderCancellationReasonReasonPlaceholder',
            )}
          />
        </div>
        <div>
          <Label required>
            {t('componentModalSupplyOrderCancellationReasonDescriptionLabel')}
          </Label>
          <Input
            isTextArea
            value={cancellationDescription}
            onChange={(value) => setCancellationDescription(value)}
            error={
              errors.cancellationDescription
                ? (errors.cancellationDescription as string)
                : ''
            }
          />
        </div>
        <div className={styles['modal-footer']}>
          <Button variant="secondary" onClick={onCancelCancellation}>
            {t('componentModalSupplyOrderCancellationReasonCancelButton')}
          </Button>
          <Button variant="primary" onClick={() => onConfirmCancellation()}>
            {t('componentModalSupplyOrderCancellationReasonConfirmButton')}
          </Button>
        </div>
      </Modal.Body>
    </Modal.Root>
  )
}
